import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Radio, RadioGroup } from '@/components/common/radio';
import { FormField } from '@/components/forms';

import { bundleMetaSource, customerMetaSource, deviceMetaSource } from '../../hooks';
import { defaultApplicability, defaultApplicableTo, PICKER_REQUIRED_MESSAGE } from '../../schemas';
import type {
  ApplicabilityFormValue,
  ApplicableToFormValue,
  DiscountFormValues,
} from '../../types';
import { AsyncMultiSelect } from '../async-multi-select';

import styles from './discount-create-form.module.scss';

const TYPE_HELP = {
  device: '* Apply this discount only to selected devices.',
  bundle: '* Apply this discount only to selected bundles.',
  customer: '* Apply this discount only to selected customers.',
} as const;

// Labels follow the AC wording (All Products / Bundle / Device); Figma 4612-1073136 showed plain
// radios with no descriptions, which we keep. Wire values are all_products/bundle/device.
const APPLICABLE_TO_OPTIONS: { value: ApplicableToFormValue['type']; label: string }[] = [
  { value: 'all_products', label: 'All Products' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'device', label: 'Device' },
];

const TYPE_OPTIONS: { value: ApplicabilityFormValue['type']; label: string }[] = [
  { value: 'device', label: 'Device' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'customer', label: 'Customer' },
];

export function ApplicabilityFields({ form }: { form: UseFormReturn<DiscountFormValues> }) {
  const applicability = form.watch('applicability');

  // Switching type/sub-type rewrites the whole sub-object to that type's empty default — clears the
  // prior selection and swaps the picker. `null`-free defaults avoid the RHF undefined→default revert.
  // Top-level switch does NOT force-validate (AC shows no error on a bare type switch); the Next gate
  // still blocks via the form's own safeParse. clearErrors is required because RHF keeps error state
  // for the unmounted picker path (shouldUnregister: false), so a previously-triggered
  // applicability.{devices,bundles,customers} error would otherwise re-render on switching back.
  const setType = (type: ApplicabilityFormValue['type']) => {
    form.clearErrors('applicability');
    form.setValue('applicability', defaultApplicability(type), { shouldDirty: true });
  };

  const setApplicableTo = (type: ApplicableToFormValue['type']) =>
    form.setValue('applicability.applicableTo', defaultApplicableTo(type), { shouldDirty: true });

  // Surface the nested picker's required error the moment a Device/Bundle sub-scope is revealed empty
  // (AC: "Please select at least one …"). We drive it with setError/clearErrors rather than trigger():
  // a validate fired as the field mounts races its registration and the deep
  // `applicability.applicableTo.{devices,bundles}` error gets pruned before it can render. The Next
  // gate is independent (the form's own safeParse), so this only governs the visible message.
  const applicableToType =
    applicability.type === 'customer' ? applicability.applicableTo.type : undefined;
  const nestedEmpty =
    applicability.type === 'customer' && applicability.applicableTo.type === 'device'
      ? !applicability.applicableTo.devices.all &&
        applicability.applicableTo.devices.ids.length === 0
      : applicability.type === 'customer' && applicability.applicableTo.type === 'bundle'
        ? !applicability.applicableTo.bundles.all &&
          applicability.applicableTo.bundles.ids.length === 0
        : false;
  useEffect(() => {
    const devices = 'applicability.applicableTo.devices' as const;
    const bundles = 'applicability.applicableTo.bundles' as const;
    // Clear only the message this effect itself owns. A server verdict lives on the same two paths
    // (`applicability.applicableTo.{devices,bundles}` are real backend field names), and Create
    // sending the admin back to Step 1 remounts this component — so an unconditional clear erased
    // the server's reason in the very commit that was meant to show it, leaving the nested
    // Applicable To arm with no message anywhere (ADO 114870).
    const clearOwn = (path: typeof devices | typeof bundles) => {
      const message = form.getFieldState(path).error?.message;
      const isOwn =
        message === undefined ||
        message === PICKER_REQUIRED_MESSAGE.device ||
        message === PICKER_REQUIRED_MESSAGE.bundle;
      if (isOwn) form.clearErrors(path);
    };
    // Always clear the inactive branch (so a stale error can't linger after a sub-option switch),
    // then set the active branch's error if its required picker is empty.
    if (applicableToType === 'device') {
      clearOwn(bundles);
      if (nestedEmpty) {
        form.setError(devices, { type: 'manual', message: PICKER_REQUIRED_MESSAGE.device });
      } else clearOwn(devices);
    } else if (applicableToType === 'bundle') {
      clearOwn(devices);
      if (nestedEmpty) {
        form.setError(bundles, { type: 'manual', message: PICKER_REQUIRED_MESSAGE.bundle });
      } else clearOwn(bundles);
    } else {
      clearOwn(devices);
      clearOwn(bundles);
    }
  }, [applicableToType, nestedEmpty, form]);

  return (
    // Deliberately a <div>/<span>, not <fieldset>/<legend>: a rendered <legend> is not a flex
    // item, so `.applicability`'s `gap` never applied between the question and its radios and the
    // two sat ~4px apart (ADO 114741). The <RadioGroup> carries its own `aria-label`, which is what
    // names the group — the same shape the nested "Applicable To" block below already uses.
    <div className={styles.applicability}>
      <span className={styles.sectionLabel}>
        Where should this discount be applied? <span className={styles.required}>*</span>
      </span>

      <RadioGroup
        aria-label="Where should this discount be applied?"
        className={styles.radios}
        value={applicability.type}
        onChange={(e) => setType(e.target.value)}
      >
        {TYPE_OPTIONS.map((o) => (
          <Radio key={o.value} value={o.value}>
            {o.label}
          </Radio>
        ))}
      </RadioGroup>

      <p className={styles.helper}>{TYPE_HELP[applicability.type]}</p>

      {applicability.type === 'device' ? (
        <FormField
          name="applicability.devices"
          control={form.control}
          label="Choose Devices"
          required
        >
          {({ field, fieldState }) => (
            <AsyncMultiSelect
              aria-label="Choose Devices"
              allLabel="All Devices"
              cacheKey="device"
              placeholder="Choose Devices"
              value={field.value}
              /* Clear a server-side Applicability error as soon as the selection changes: these
                 pickers wire no `field.onBlur`, so RHF's `onTouched` mode never revalidates
                 them and a 422 detail would stick forever (ADO 114870). */
              onChange={(next) => {
                field.onChange(next);
                form.clearErrors(field.name);
              }}
              status={fieldState.error ? 'error' : undefined}
              {...deviceMetaSource}
            />
          )}
        </FormField>
      ) : null}

      {applicability.type === 'bundle' ? (
        <FormField
          name="applicability.bundles"
          control={form.control}
          label="Choose Bundles"
          required
        >
          {({ field, fieldState }) => (
            <AsyncMultiSelect
              aria-label="Choose Bundles"
              allLabel="All Bundles"
              cacheKey="bundle"
              placeholder="Choose Bundles"
              value={field.value}
              onChange={(next) => {
                field.onChange(next);
                form.clearErrors(field.name);
              }}
              status={fieldState.error ? 'error' : undefined}
              {...bundleMetaSource}
            />
          )}
        </FormField>
      ) : null}

      {applicability.type === 'customer' ? (
        <>
          <FormField
            name="applicability.customers"
            control={form.control}
            label="Choose Customers"
            required
          >
            {({ field, fieldState }) => (
              <AsyncMultiSelect
                aria-label="Choose Customers"
                allLabel="All Customers"
                cacheKey="customer"
                placeholder="Choose Customers"
                value={field.value}
                onChange={(next) => {
                  field.onChange(next);
                  form.clearErrors(field.name);
                }}
                status={fieldState.error ? 'error' : undefined}
                {...customerMetaSource}
              />
            )}
          </FormField>

          <div className={styles.applicableTo}>
            <span className={styles.sectionLabel}>
              Applicable To <span className={styles.required}>*</span>
            </span>
            <RadioGroup
              aria-label="Applicable To"
              className={styles.radios}
              value={applicability.applicableTo.type}
              onChange={(e) => setApplicableTo(e.target.value)}
            >
              {APPLICABLE_TO_OPTIONS.map((o) => (
                <Radio key={o.value} value={o.value}>
                  {o.label}
                </Radio>
              ))}
            </RadioGroup>

            {applicability.applicableTo.type === 'device' ? (
              <FormField
                name="applicability.applicableTo.devices"
                control={form.control}
                label="Choose Devices"
                required
              >
                {({ field, fieldState }) => (
                  <AsyncMultiSelect
                    aria-label="Choose Applicable Devices"
                    allLabel="All Devices"
                    cacheKey="device"
                    placeholder="Choose Devices"
                    value={field.value}
                    onChange={(next) => {
                      field.onChange(next);
                      form.clearErrors(field.name);
                    }}
                    status={fieldState.error ? 'error' : undefined}
                    {...deviceMetaSource}
                  />
                )}
              </FormField>
            ) : null}

            {applicability.applicableTo.type === 'bundle' ? (
              <FormField
                name="applicability.applicableTo.bundles"
                control={form.control}
                label="Choose Bundles"
                required
              >
                {({ field, fieldState }) => (
                  <AsyncMultiSelect
                    aria-label="Choose Applicable Bundles"
                    allLabel="All Bundles"
                    cacheKey="bundle"
                    placeholder="Choose Bundles"
                    value={field.value}
                    onChange={(next) => {
                      field.onChange(next);
                      form.clearErrors(field.name);
                    }}
                    status={fieldState.error ? 'error' : undefined}
                    {...bundleMetaSource}
                  />
                )}
              </FormField>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
